import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Button, Dialog, IssueIcon, Label, Modal, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { User } from '../../users/types'
import { useCycleStore } from '../store'
import type { CycleViewer, CycleViewerSearchInput } from './types'
import { ViewerScopeModal } from './ViewerScopeModal'

type UserSearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

export function CycleViewersTab() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [selectedViewersToDelete, setSelectedViewersToDelete] = useState<CycleViewer[]>([])
	const [scopeModalViewer, setScopeModalViewer] = useState<CycleViewer | null>(null)
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const cycle = useCycleStore((state) => state.cycle)

	const addCycleViewersMutation = useMutation({
		...trpc.admin.cyclesViewers.create.mutationOptions(),
		onSuccess: () => {
			showToast('User(s) set as cycle viewer(s)')
			setIsModalOpen(false)
			setSelectedUsers([])
		},
		onError: () => {
			showToast('Something went wrong, the user(s) was(were) not set as a cycle viewer(s)', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.search.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const deleteCycleViewersMutation = useMutation({
		...trpc.admin.cyclesViewers.delete.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle viewer(s) removed')
			setSelectedViewersToDelete([])
			setIsDeleteDialogOpen(false)
		},
		onError: () => {
			showToast('Something went wrong, the cycle viewer(s) was(were) not removed', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.search.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	useEffect(() => {
		if (isModalOpen) {
			queryClient.invalidateQueries({
				queryKey: [['cyclesViewers', 'searchPossibleViewers']],
				exact: false,
				refetchType: 'all',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isModalOpen, queryClient.invalidateQueries])

	const handleCloseModal = () => {
		setIsModalOpen(false)
		setSelectedUsers([])
	}

	const handleAddSelected = () => {
		if (!cycle || !organizationId) return
		addCycleViewersMutation.mutate({
			cycleId: cycle.id,
			organizationId,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleDeleteSelected = () => {
		if (!cycle || !organizationId) return
		deleteCycleViewersMutation.mutate({
			cycleId: cycle.id,
			organizationId,
			userIds: selectedViewersToDelete.map((viewer) => viewer.userId),
		})
	}

	const handleOpenDeleteDialog = () => {
		setIsDeleteDialogOpen(true)
	}

	const handleCloseDeleteDialog = () => {
		setIsDeleteDialogOpen(false)
	}

	return (
		<div className='mt-6'>
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl'>
				<GenericTable<CycleViewer, CycleViewerSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.cyclesViewers.search.queryOptions({
							...options,
							organizationId: organizationId as string,
							cycleId: cycle?.id || '',
						}),
						enabled: !!cycle?.id,
					})}
					selectable
					onSelectionChange={setSelectedViewersToDelete}
					columns={[
						{
							field: 'firstName',
							label: 'Name',
							colSpan: 2,
							render: (viewer) => (
								<Text variant='xsmall' weight='regular'>
									{`${viewer.firstName} ${viewer.lastName}`}
								</Text>
							),
						},
						{
							field: 'externalId',
							label: 'ID',
							colSpan: 1,
						},
						{
							field: 'email',
							label: 'Email',
							colSpan: 2,
							render: (viewer) => (
								<Text variant='xsmall' weight='regular'>
									{viewer.email}
								</Text>
							),
						},
						{
							field: 'active',
							label: 'Status',
							colSpan: 1,
							render: (viewer) => (
								<div className='flex'>
									<Label className='self-start' color={viewer.active ? 'green' : 'default'}>
										{viewer.active ? 'Active' : 'Inactive'}
									</Label>
								</div>
							),
						},
						{
							field: 'scopeType' as keyof CycleViewer,
							label: 'Scope',
							colSpan: 1,
							render: (viewer: CycleViewer) => (
								<Text variant='xsmall' weight='regular'>
									{viewer.scopeType === 'all' ? 'All' : viewer.scopeType === 'hierarchy' ? 'Hierarchy' : 'Assigned'}
								</Text>
							),
						},
						{
							field: 'id' as keyof CycleViewer,
							label: '',
							colSpan: 1,
							sortable: false,
							render: (viewer: CycleViewer) => (
								<Button
									variant='tertiary'
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										setScopeModalViewer(viewer)
									}}>
									Configure
								</Button>
							),
						},
					]}
					showPagination
					actionButtons={[
						{
							children: 'Add user viewer',
							onClick: handleOpenModal,
						},
					]}
					emptyState={{
						title: 'No cycle viewers added yet',
						description: 'Get started by adding cycle viewers',
						actionLabel: 'Add cycle viewer',
						icon: IssueIcon,
						onAction: handleOpenModal,
					}}
				/>
			</div>

			<Modal isOpen={isModalOpen} fullContent onClose={handleCloseModal} fullScreen title='Add Cycle Viewers'>
				<div className='flex flex-col h-[calc(100vh-150px)]'>
					<GenericTable<User, UserSearchInput>
						queryOptionsGenerator={(options) => ({
							...trpc.admin.cyclesViewers.searchPossibleViewers.queryOptions({
								...options,
								organizationId: organizationId as string,
								cycleId: cycle?.id as string,
							}),
							enabled: !!cycle?.id && isModalOpen,
						})}
						isInternal
						selectable
						onSelectionChange={setSelectedUsers}
						columns={[
							{
								field: 'firstName',
								label: 'Name',
								colSpan: 2,
								render: (user) => (
									<Text variant='xsmall' weight='regular'>
										{`${user.firstName} ${user.lastName}`}
									</Text>
								),
							},
							{
								field: 'externalId',
								label: 'ID',
								colSpan: 1,
							},
							{
								field: 'email',
								label: 'Email',
								colSpan: 2,
								render: (user) => (
									<Text variant='xsmall' weight='regular'>
										{user.email}
									</Text>
								),
							},
							{
								field: 'active',
								label: 'Status',
								colSpan: 1,
								render: (user) => (
									<div className='flex'>
										<Label className='self-start' color={user.active ? 'green' : 'default'}>
											{user.active ? 'Active' : 'Inactive'}
										</Label>
									</div>
								),
							},
						]}
					/>
				</div>
				<ActionBar
					variant='modal'
					className='left-0 flex justify-end'
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: handleCloseModal,
						},
						{
							label: `Add user viewers${selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ''}`,
							variant: 'primary',
							onClick: handleAddSelected,
							disabled: selectedUsers.length === 0,
						},
					]}
				/>
			</Modal>

			{selectedViewersToDelete.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedViewersToDelete([]),
						},
						{
							label: `Delete${selectedViewersToDelete.length > 0 ? ` (${selectedViewersToDelete.length})` : ''}`,
							variant: 'destructive',
							onClick: handleOpenDeleteDialog,
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={handleCloseDeleteDialog}
				onConfirm={handleDeleteSelected}
				title='Delete cycle viewers'
				message={`Are you sure you want to delete ${selectedViewersToDelete.length} cycle viewer${selectedViewersToDelete.length === 1 ? '' : 's'}?`}
				type='delete'
				confirmLabel='Delete'
			/>

			{scopeModalViewer && organizationId && (
				<ViewerScopeModal
					isOpen={!!scopeModalViewer}
					onClose={() => setScopeModalViewer(null)}
					viewer={scopeModalViewer}
					organizationId={organizationId}
					cycleId={cycle?.id || ''}
				/>
			)}
		</div>
	)
}
